import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, 
  ArrowRight, 
  Upload, 
  X, 
  Image as ImageIcon,
  Sparkles,
  CheckCircle,
  Loader2,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import imageCompression from 'browser-image-compression';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, name: 'Property Details', description: 'Basic property information' },
  { id: 2, name: 'Photos', description: 'Upload listing photos' },
  { id: 3, name: 'Preferences', description: 'Tone and language' },
  { id: 4, name: 'Results', description: 'Review and copy' }
];

const CreateListing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [listingId, setListingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zip_code: '',
    neighborhood: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    lot_size: '',
    year_built: '',
    property_type: 'single_family',
    remarks: '',
    tone: 'Professional',
    language_output: 'EN'
  });
  
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [enhancingAll, setEnhancingAll] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState(null);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Helper function to get all files from a directory entry recursively
  const getFilesFromDirectory = async (dirEntry) => {
    const files = [];
    const reader = dirEntry.createReader();
    
    const readEntries = () => new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    
    let entries = await readEntries();
    while (entries.length > 0) {
      for (const entry of entries) {
        if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          // Only add image files
          if (file.type.startsWith('image/') || 
              file.name.toLowerCase().endsWith('.heic') ||
              file.name.toLowerCase().endsWith('.dng') ||
              file.name.toLowerCase().endsWith('.raw')) {
            files.push(file);
          }
        } else if (entry.isDirectory) {
          const subFiles = await getFilesFromDirectory(entry);
          files.push(...subFiles);
        }
      }
      entries = await readEntries();
    }
    return files;
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (!listingId) {
      toast.error('Please complete Step 1 first');
      return;
    }
    
    const items = e.dataTransfer.items;
    const files = [];
    
    // Process dropped items (can be files or folders)
    const processPromises = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.();
        if (entry) {
          if (entry.isDirectory) {
            // It's a folder - get all files recursively
            processPromises.push(getFilesFromDirectory(entry));
          } else {
            // It's a file
            const file = item.getAsFile();
            if (file && (file.type.startsWith('image/') || 
                file.name.toLowerCase().endsWith('.heic') ||
                file.name.toLowerCase().endsWith('.dng') ||
                file.name.toLowerCase().endsWith('.raw'))) {
              files.push(file);
            }
          }
        } else {
          // Fallback for browsers that don't support webkitGetAsEntry
          const file = item.getAsFile();
          if (file && file.type.startsWith('image/')) {
            files.push(file);
          }
        }
      }
    }
    
    // Wait for all directory processing to complete
    const directoryFiles = await Promise.all(processPromises);
    directoryFiles.forEach(dirFiles => files.push(...dirFiles));
    
    if (files.length === 0) {
      toast.error('No valid image files found');
      return;
    }
    
    toast.info(`Found ${files.length} image${files.length > 1 ? 's' : ''} to upload`);
    
    // Store files to be processed by useEffect
    setDroppedFiles(files);
  }, [listingId]);

  // Load Google Places API
  useEffect(() => {
    if (window.google?.maps?.places) {
      setGoogleLoaded(true);
      return;
    }
    
    // Check if script already exists
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkGoogle = setInterval(() => {
        if (window.google?.maps?.places) {
          setGoogleLoaded(true);
          clearInterval(checkGoogle);
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      const checkPlaces = setInterval(() => {
        if (window.google?.maps?.places) {
          setGoogleLoaded(true);
          clearInterval(checkPlaces);
        }
      }, 100);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!googleLoaded || !addressInputRef.current || autocompleteRef.current) return;
    
    try {
      // Try standard Autocomplete first
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { 
          types: ['address'],
          componentRestrictions: { country: 'us' }
        }
      );
      
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.address_components) return;
        
        let streetNumber = '';
        let streetName = '';
        let city = '';
        let state = '';
        let zipCode = '';
        let neighborhood = '';
        
        place.address_components.forEach(component => {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              streetNumber = component.long_name;
              break;
            case 'route':
              streetName = component.long_name;
              break;
            case 'locality':
              city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.short_name;
              break;
            case 'postal_code':
              zipCode = component.long_name;
              break;
            case 'neighborhood':
            case 'sublocality_level_1':
              neighborhood = component.long_name;
              break;
            default:
              break;
          }
        });
        
        setFormData(prev => ({
          ...prev,
          address: `${streetNumber} ${streetName}`.trim(),
          city,
          state,
          zip_code: zipCode,
          neighborhood: neighborhood || prev.neighborhood
        }));
        
        toast.success('Address filled from Google');
      });
    } catch (error) {
      console.error('Google Places error:', error);
    }
  }, [googleLoaded]);

  const propertyTypes = [
    { value: 'single_family', label: 'Single Family' },
    { value: 'condo', label: 'Condo' },
    { value: 'townhome', label: 'Townhome' },
    { value: 'multifamily', label: 'Multi-Family' },
    { value: 'land', label: 'Land' },
    { value: 'other', label: 'Other' }
  ];

  const tones = [
    { value: 'Professional', label: 'Professional', description: 'Clean, straightforward, business-like' },
    { value: 'Luxury', label: 'Luxury', description: 'Sophisticated, upscale, elegant' },
    { value: 'Investor', label: 'Investor', description: 'ROI-focused, analytical, data-driven' },
    { value: 'Friendly', label: 'Friendly', description: 'Warm, welcoming, approachable' }
  ];

  // Step 1: Create Listing
  const handleCreateListing = async () => {
    if (!formData.address || !formData.city || !formData.state || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/listings`, {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseFloat(formData.bathrooms) || 0,
        sqft: parseInt(formData.sqft) || 0,
        lot_size: formData.lot_size || null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null
      }, { headers: getAuthHeaders() });
      
      setListingId(response.data.id);
      setCurrentStep(2);
      toast.success('Listing created! Now add photos.');
    } catch (error) {
      console.error('Create listing error:', error);
      const message = error.response?.data?.detail || 'Failed to create listing';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Upload Photos - with parallel processing for faster uploads
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    
    // Compression options - backend resizes to 1400px, so no need to send large files
    const compressionOptions = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.8
    };
    
    // Process and compress files
    const processedFiles = [];
    const processingToast = toast.loading(`Processing ${files.length} photo${files.length > 1 ? 's' : ''}...`);
    
    for (const file of files) {
      const fileSizeMB = file.size / (1024 * 1024);
      const fileName = file.name.toLowerCase();
      
      // DNG/RAW files - send directly to server for conversion (up to 100MB)
      if (fileName.endsWith('.dng') || fileName.endsWith('.raw') || fileName.endsWith('.cr2') || fileName.endsWith('.nef')) {
        if (fileSizeMB > 100) {
          toast.error(`${file.name} is too large (${fileSizeMB.toFixed(0)}MB). Max 100MB for RAW files.`);
          continue;
        }
        processedFiles.push(file);
        continue;
      }
      
      const needsCompression = fileSizeMB > 1.5 || fileName.endsWith('.heic');
      
      try {
        if (needsCompression) {
          // Compress large files and convert HEIC to JPEG
          const compressedFile = await imageCompression(file, compressionOptions);
          const newSizeMB = compressedFile.size / (1024 * 1024);
          console.log(`Compressed ${file.name}: ${fileSizeMB.toFixed(1)}MB → ${newSizeMB.toFixed(1)}MB`);
          processedFiles.push(compressedFile);
        } else {
          processedFiles.push(file);
        }
      } catch (compressionError) {
        console.error(`Failed to process ${file.name}:`, compressionError);
        toast.error(`Could not process ${file.name}. Try JPG or PNG format.`);
      }
    }
    
    toast.dismiss(processingToast);
    
    if (processedFiles.length === 0) {
      setUploadingPhoto(false);
      return;
    }
    
    // Show progress toast
    const uploadingToast = toast.loading(`Uploading ${processedFiles.length} photo${processedFiles.length > 1 ? 's' : ''}...`);
    
    // Upload function for a single file
    const uploadSingleFile = async (file) => {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      
      const response = await axios.post(
        `${API}/listings/${listingId}/photos`,
        formDataFile,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 120 second timeout for large files
        }
      );
      
      // The upload response already contains the photo data
      return {
        id: response.data.id,
        original_url: response.data.original_photo_url,
        enhanced_url: response.data.enhanced_photo_url,
        thumbnail_url: response.data.thumbnail_url,
        enhancing: false
      };
    };
    
    // Process files in batches of 3 for parallel upload
    const batchSize = 3;
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < processedFiles.length; i += batchSize) {
      const batch = processedFiles.slice(i, i + batchSize);
      
      const results = await Promise.allSettled(
        batch.map(file => uploadSingleFile(file).catch(err => {
          console.error(`Upload error for ${file.name}:`, err);
          throw { fileName: file.name, error: err };
        }))
      );
      
      for (const result of results) {
        if (result.status === 'fulfilled') {
          setPhotos(prev => [...prev, result.value]);
          successCount++;
        } else {
          failCount++;
          const fileName = result.reason?.fileName || 'Unknown file';
          const errorMsg = result.reason?.error?.response?.data?.detail || 'Upload failed';
          toast.error(`Failed to upload ${fileName}: ${errorMsg}`);
        }
      }
    }
    
    toast.dismiss(uploadingToast);
    
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} photo${successCount > 1 ? 's' : ''} successfully!`);
    }
    if (failCount > 0 && successCount === 0) {
      toast.error(`Failed to upload ${failCount} photo${failCount > 1 ? 's' : ''}`);
    }
    
    setUploadingPhoto(false);
  };

  // Handle dropped files from drag and drop
  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0 && listingId) {
      const fakeEvent = { target: { files: droppedFiles } };
      handlePhotoUpload(fakeEvent);
      setDroppedFiles(null);
    }
  }, [droppedFiles, listingId]);

  const handleEnhancePhoto = async (photoId) => {
    setPhotos(prev => prev.map(p => 
      p.id === photoId ? { ...p, enhancing: true } : p
    ));
    
    try {
      await axios.post(
        `${API}/listings/${listingId}/photos/${photoId}/enhance`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Fetch updated photo
      const photoData = await axios.get(
        `${API}/photos/${photoId}/data`,
        { headers: getAuthHeaders() }
      );
      
      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { 
          ...p, 
          enhanced_url: photoData.data.enhanced_photo_url,
          enhancing: false 
        } : p
      ));
      
      toast.success('Photo enhanced!');
    } catch (error) {
      console.error('Enhance error:', error);
      toast.error('Failed to enhance photo');
      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, enhancing: false } : p
      ));
    }
  };

  const handleRemovePhoto = async (photoId) => {
    try {
      await axios.delete(
        `${API}/listings/${listingId}/photos/${photoId}`,
        { headers: getAuthHeaders() }
      );
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo removed');
    } catch (error) {
      console.error('Remove photo error:', error);
      toast.error('Failed to remove photo');
    }
  };

  // Enhance all unenhanced photos
  const handleEnhanceAllPhotos = async () => {
    const unenhancedPhotos = photos.filter(p => !p.enhanced_url && !p.enhancing);
    if (unenhancedPhotos.length === 0) {
      toast.info('All photos are already enhanced');
      return;
    }

    setEnhancingAll(true);
    
    // Mark all as enhancing
    setPhotos(prev => prev.map(p => 
      !p.enhanced_url ? { ...p, enhancing: true } : p
    ));

    let enhancedCount = 0;
    
    for (const photo of unenhancedPhotos) {
      try {
        await axios.post(
          `${API}/listings/${listingId}/photos/${photo.id}/enhance`,
          {},
          { headers: getAuthHeaders() }
        );
        
        // Fetch updated photo
        const photoData = await axios.get(
          `${API}/photos/${photo.id}/data`,
          { headers: getAuthHeaders() }
        );
        
        setPhotos(prev => prev.map(p => 
          p.id === photo.id ? { 
            ...p, 
            enhanced_url: photoData.data.enhanced_photo_url,
            enhancing: false 
          } : p
        ));
        
        enhancedCount++;
      } catch (error) {
        console.error(`Failed to enhance photo ${photo.id}:`, error);
        setPhotos(prev => prev.map(p => 
          p.id === photo.id ? { ...p, enhancing: false } : p
        ));
      }
    }
    
    setEnhancingAll(false);
    
    if (enhancedCount > 0) {
      toast.success(`Enhanced ${enhancedCount} photo${enhancedCount > 1 ? 's' : ''}!`);
    } else {
      toast.error('Failed to enhance photos. Please try again.');
    }
  };

  // Step 3: Update Preferences and Generate Content
  const handleUpdatePreferences = async () => {
    if (!listingId) {
      toast.error('Listing not found. Please start from Step 1.');
      setCurrentStep(1);
      return;
    }
    
    setLoading(true);
    try {
      // Save preferences
      await axios.put(`${API}/listings/${listingId}`, {
        tone: formData.tone,
        language_output: formData.language_output
      }, { headers: getAuthHeaders() });
      
      // Start generation immediately (skip step 4 confirmation)
      setLoading(false);
      setGenerating(true);
      setGenerationProgress(0);
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 10, 90));
      }, 2000);
      
      // Generate content with longer timeout for Modal processing
      await axios.post(
        `${API}/listings/${listingId}/generate`,
        {},
        { 
          headers: getAuthHeaders(),
          timeout: 120000  // 2 minute timeout for Modal AI
        }
      );
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      setTimeout(() => {
        toast.success('Content generated successfully!');
        navigate(`/listings/${listingId}`);
      }, 500);
    } catch (error) {
      console.error('Preferences/Generate error:', error);
      const message = error.response?.data?.detail || 'Failed to generate content. Please try again.';
      toast.error(message);
      setGenerating(false);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Generate Content (kept for direct access if needed)
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(0);
    
    // Simulate progress
    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => Math.min(prev + 10, 90));
    }, 2000);
    
    try {
      await axios.post(
        `${API}/listings/${listingId}/generate`,
        {},
        { 
          headers: getAuthHeaders(),
          timeout: 120000  // 2 minute timeout for Modal AI
        }
      );
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      setTimeout(() => {
        toast.success('Content generated successfully!');
        navigate(`/listings/${listingId}`);
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Generate error:', error);
      const message = error.response?.data?.detail || 'Failed to generate content';
      toast.error(message);
      setGenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="address">Street Address *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    ref={addressInputRef}
                    id="address"
                    placeholder="Start typing address..."
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="input-address"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Start typing to auto-complete from Google
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="Los Angeles"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="input-city"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    placeholder="CA"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    data-testid="input-state"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    placeholder="90210"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    data-testid="input-zip"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="neighborhood">Neighborhood/Area</Label>
                <Input
                  id="neighborhood"
                  placeholder="Beverly Hills"
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  data-testid="input-neighborhood"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price">Price *</Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="500000"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  data-testid="input-price"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  placeholder="3"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                  data-testid="input-bedrooms"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  step="0.5"
                  placeholder="2"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                  data-testid="input-bathrooms"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sqft">Square Feet</Label>
                <Input
                  id="sqft"
                  type="number"
                  placeholder="2000"
                  value={formData.sqft}
                  onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                  data-testid="input-sqft"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lot_size">Lot Size</Label>
                <Input
                  id="lot_size"
                  placeholder="0.25 acres or 10,000 sqft"
                  value={formData.lot_size}
                  onChange={(e) => setFormData({ ...formData, lot_size: e.target.value })}
                  data-testid="input-lot-size"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="year_built">Year Built</Label>
                <Input
                  id="year_built"
                  type="number"
                  placeholder="2005"
                  value={formData.year_built}
                  onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
                  data-testid="input-year-built"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type</Label>
                <Select 
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger data-testid="select-property-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="remarks">Remarks</Label>
                <p className="text-sm text-muted-foreground">
                  Write down the main features you want to highlight and any other details you want to make sure come out in the remarks.
                </p>
                <Textarea
                  id="remarks"
                  placeholder="Updated kitchen with granite countertops, hardwood floors throughout, large backyard with pool, close to schools and shopping..."
                  rows={4}
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  data-testid="input-remarks"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label>Upload Photos</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Add photos of your listing.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Tip:</strong> You can drag & drop photos or entire folders here. Photos will continue uploading in the background when you proceed to the next step.
                </p>
              </div>
              
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                  isDragging 
                    ? 'border-primary bg-primary/10 scale-[1.02]' 
                    : 'border-border hover:border-primary/50'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                data-testid="photo-drop-zone"
              >
                <input
                  type="file"
                  accept="image/*,.heic,.dng,.raw,.cr2,.nef"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                  disabled={uploadingPhoto}
                />
                <label 
                  htmlFor="photo-upload" 
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-12 w-12 text-muted-foreground animate-spin" />
                  ) : isDragging ? (
                    <Upload className="h-12 w-12 text-primary animate-bounce" />
                  ) : (
                    <Upload className="h-12 w-12 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium text-foreground">
                      {uploadingPhoto ? 'Uploading...' : isDragging ? 'Drop files here!' : 'Drag & drop photos or folders here'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {isDragging ? 'Release to upload' : 'or click to browse • All formats supported'}
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            {photos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div 
                    key={photo.id} 
                    className="relative group aspect-[4/3] rounded-xl overflow-hidden bg-muted"
                  >
                    <img
                      src={photo.enhanced_url || photo.original_url}
                      alt="Listing photo"
                      className="w-full h-full object-cover"
                    />
                    
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {!photo.enhanced_url && !photo.enhancing && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleEnhancePhoto(photo.id)}
                          className="gap-1"
                          data-testid={`enhance-photo-${photo.id}`}
                        >
                          <Sparkles size={14} />
                          Enhance
                        </Button>
                      )}
                      {photo.enhancing && (
                        <div className="flex items-center gap-2 text-white">
                          <Loader2 size={16} className="animate-spin" />
                          Enhancing...
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemovePhoto(photo.id)}
                        data-testid={`remove-photo-${photo.id}`}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                    
                    {photo.enhanced_url && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                        <CheckCircle size={12} />
                        Enhanced
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 3:
        // Show generating overlay if generation is in progress
        if (generating) {
          return (
            <div className="text-center py-12">
              <div className="space-y-6">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="h-10 w-10 text-primary animate-pulse" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                    Generating Your Marketing Package
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    AI is creating MLS remarks, social posts, email copy, and more...
                  </p>
                  <div className="max-w-xs mx-auto">
                    <Progress value={generationProgress} className="h-2" />
                    <p className="text-sm text-muted-foreground mt-2">{generationProgress}%</p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <Label>Content Tone</Label>
              <p className="text-sm text-muted-foreground mb-4">
                Choose the writing style for your marketing content.
              </p>
              <RadioGroup
                value={formData.tone}
                onValueChange={(value) => setFormData({ ...formData, tone: value })}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                {tones.map((tone) => (
                  <div key={tone.value}>
                    <RadioGroupItem value={tone.value} id={tone.value} className="peer sr-only" />
                    <Label
                      htmlFor={tone.value}
                      className="flex flex-col p-4 rounded-xl border-2 border-border bg-white hover:bg-secondary/30 peer-data-[state=checked]:border-primary cursor-pointer transition-all"
                      data-testid={`tone-${tone.value.toLowerCase()}`}
                    >
                      <span className="font-semibold text-foreground">{tone.label}</span>
                      <span className="text-sm text-muted-foreground">{tone.description}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <Label>Language Output</Label>
              <p className="text-sm text-muted-foreground mb-4">
                Select which languages to generate content in.
              </p>
              <RadioGroup
                value={formData.language_output}
                onValueChange={(value) => setFormData({ ...formData, language_output: value })}
                className="flex flex-wrap gap-4"
              >
                <div>
                  <RadioGroupItem value="EN" id="lang-en" className="peer sr-only" />
                  <Label
                    htmlFor="lang-en"
                    className="flex items-center gap-2 px-4 py-3 rounded-full border-2 border-border bg-white hover:bg-secondary/30 peer-data-[state=checked]:border-primary cursor-pointer transition-all"
                    data-testid="lang-en"
                  >
                    English Only
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="ES" id="lang-es" className="peer sr-only" />
                  <Label
                    htmlFor="lang-es"
                    className="flex items-center gap-2 px-4 py-3 rounded-full border-2 border-border bg-white hover:bg-secondary/30 peer-data-[state=checked]:border-primary cursor-pointer transition-all"
                    data-testid="lang-es"
                  >
                    Spanish Only
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="BOTH" id="lang-both" className="peer sr-only" />
                  <Label
                    htmlFor="lang-both"
                    className="flex items-center gap-2 px-4 py-3 rounded-full border-2 border-border bg-white hover:bg-secondary/30 peer-data-[state=checked]:border-primary cursor-pointer transition-all"
                    data-testid="lang-both"
                  >
                    Both Languages
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <Navbar variant="app" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => currentStep === 1 ? navigate('/dashboard') : setCurrentStep(currentStep - 1)}
          className="mb-6 gap-2"
          disabled={loading || generating}
          data-testid="back-btn"
        >
          <ArrowLeft size={16} />
          {currentStep === 1 ? 'Back to Dashboard' : 'Previous Step'}
        </Button>

        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`wizard-step ${
                    currentStep === step.id ? 'wizard-step-active' :
                    currentStep > step.id ? 'wizard-step-completed' : 'wizard-step-inactive'
                  }`}>
                    {currentStep > step.id ? <CheckCircle size={16} /> : step.id}
                  </div>
                  <span className="text-xs mt-2 hidden sm:block text-muted-foreground">
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-full h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-border'
                  }`} style={{ minWidth: '2rem' }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card className="border-border/40" data-testid="wizard-card">
          <CardHeader>
            <CardTitle className="font-heading">{STEPS[currentStep - 1].name}</CardTitle>
            <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        {currentStep < 4 && (
          <div className="flex justify-end mt-6">
            <Button
              onClick={() => {
                if (currentStep === 1) handleCreateListing();
                else if (currentStep === 2) setCurrentStep(3);
                else if (currentStep === 3) handleUpdatePreferences();
              }}
              disabled={loading || generating}
              className="rounded-full gap-2"
              data-testid="next-btn"
            >
              {loading ? 'Saving...' : generating ? 'Generating...' : currentStep === 3 ? (
                <>
                  <Sparkles size={16} />
                  Generate Marketing Package
                </>
              ) : (
                <>
                  Next Step
                  <ArrowRight size={16} />
                </>
              )}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreateListing;
